/**
 * TaskDependencyTab — Cytoscape.js mini-DAG for task dependency visualization.
 *
 * Shows the current task (center, highlighted) with:
 * - Upstream dependencies[] rendered as parent nodes with edges flowing IN
 * - Downstream dependents[] rendered as child nodes with edges flowing OUT
 * - Click a node → switch to that task via onTaskSelect callback
 *
 * Reuses shared styles from cytoscape-task-styles.ts and task-status-colors.ts.
 * Only visible for: task primitive type (via CONTEXTUAL_TABS).
 *
 * DUMB FRONTEND: All data from TaskFieldsFragment. No hardcoded assumptions.
 */

import { useEffect, useRef, useMemo } from 'react';
import {
  Stack,
  Text,
  Center,
  Group,
  Badge,
  Paper,
  Box,
} from '@mantine/core';
import {
  IconGitBranch,
  IconArrowRight,
  IconArrowLeft,
} from '@tabler/icons-react';
import cytoscape, { type Core } from 'cytoscape';
import type { TaskFieldsFragment } from '@/generated/graphql';
import { getTaskStatusColor, DEFAULT_STATUS_COLOR } from '../task-status-colors';
import { buildTaskCytoscapeStyles, DEPENDENCY_EDGE_STYLES } from '../cytoscape-task-styles';

// Build styles once at module level
const CYTOSCAPE_STYLES = buildTaskCytoscapeStyles(DEPENDENCY_EDGE_STYLES);

// ============================================================================
// COMPONENT
// ============================================================================

interface TaskDependencyTabProps {
  /** The currently selected task */
  currentTask: TaskFieldsFragment | null;
  /** All tasks in the workflow (siblings) for name/status resolution */
  allTasks: TaskFieldsFragment[];
  /** Callback when a dependency node is clicked */
  onTaskSelect?: (task: TaskFieldsFragment) => void;
}

export function TaskDependencyTab({
  currentTask,
  allTasks,
  onTaskSelect,
}: TaskDependencyTabProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);

  // Build a lookup map of all tasks by ID
  const taskMap = useMemo(() => {
    const map = new Map<string, TaskFieldsFragment>();
    for (const task of allTasks) {
      map.set(task.id, task);
    }
    return map;
  }, [allTasks]);

  // Build Cytoscape elements from the task's dependencies/dependents
  const elements = useMemo(() => {
    if (!currentTask) return { nodes: [], edges: [] };

    const nodes: cytoscape.ElementDefinition[] = [];
    const edges: cytoscape.ElementDefinition[] = [];
    const addedNodes = new Set<string>();

    // Helper to add a task node
    const addNode = (taskId: string, isCurrent: boolean) => {
      if (addedNodes.has(taskId)) return;
      addedNodes.add(taskId);

      const task = taskMap.get(taskId);
      const colors = task
        ? getTaskStatusColor(task.status)
        : DEFAULT_STATUS_COLOR;
      const label = task ? truncateLabel(task.name) : taskId.slice(-8);

      nodes.push({
        data: {
          id: taskId,
          label,
          bg: colors.bg,
          borderColor: colors.border,
          isCurrent,
          status: task?.status ?? 'UNKNOWN',
        },
      });
    };

    // Add current task as center node
    addNode(currentTask.id, true);

    // Add upstream dependencies (tasks this task depends on)
    for (const depId of currentTask.dependencies) {
      addNode(depId, false);
      edges.push({
        data: {
          id: `dep-${depId}-${currentTask.id}`,
          source: depId,
          target: currentTask.id,
          isUpstream: true,
          isDownstream: false,
        },
      });
    }

    // Add downstream dependents (tasks that depend on this task)
    for (const depId of currentTask.dependents) {
      addNode(depId, false);
      edges.push({
        data: {
          id: `dep-${currentTask.id}-${depId}`,
          source: currentTask.id,
          target: depId,
          isUpstream: false,
          isDownstream: true,
        },
      });
    }

    return { nodes, edges };
  }, [currentTask, taskMap]);

  // Initialize and update Cytoscape
  useEffect(() => {
    if (!containerRef.current || elements.nodes.length === 0) {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
      return;
    }

    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = null;
    }

    const cy = cytoscape({
      container: containerRef.current,
      elements: [...elements.nodes, ...elements.edges],
      style: CYTOSCAPE_STYLES as cytoscape.StylesheetStyle[],
      layout: {
        name: 'breadthfirst',
        directed: true,
        spacingFactor: 1.5,
        avoidOverlap: true,
        roots: elements.nodes
          .filter(n => {
            const nodeId = n.data.id;
            return !elements.edges.some(e => e.data.target === nodeId);
          })
          .map(n => n.data.id as string),
      },
      userZoomingEnabled: true,
      userPanningEnabled: true,
      boxSelectionEnabled: false,
      autoungrabify: true,
      minZoom: 0.5,
      maxZoom: 2.0,
    });

    cy.on('tap', 'node', (event) => {
      const nodeId = event.target.id();
      if (nodeId === currentTask?.id) return;
      const task = taskMap.get(nodeId);
      if (task && onTaskSelect) {
        onTaskSelect(task);
      }
    });

    cy.on('mouseover', 'node', (event) => {
      const nodeId = event.target.id();
      if (nodeId !== currentTask?.id) {
        containerRef.current!.style.cursor = 'pointer';
      }
    });
    cy.on('mouseout', 'node', () => {
      containerRef.current!.style.cursor = 'default';
    });

    cyRef.current = cy;

    cy.on('layoutstop', () => {
      cy.fit(undefined, 30);
    });

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [elements, currentTask, taskMap, onTaskSelect]);

  // Empty state
  if (!currentTask) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select a task to view dependencies.</Text>
      </Center>
    );
  }

  const hasDeps = currentTask.dependencies.length > 0;
  const hasDepnts = currentTask.dependents.length > 0;
  const hasDependencyData = hasDeps || hasDepnts;

  if (!hasDependencyData) {
    return (
      <Center py="xl">
        <Stack align="center" gap="xs">
          <IconGitBranch size={32} color="var(--mantine-color-dimmed)" />
          <Text c="dimmed" size="sm">No dependencies or dependents.</Text>
          <Text c="dimmed" size="xs">
            This task runs independently within the workflow.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="sm" py="xs">
      {/* Stats bar */}
      <Group gap="xs" px="xs">
        <Badge
          variant="light"
          color="blue"
          size="sm"
          leftSection={<IconArrowLeft size={12} />}
        >
          {currentTask.dependencies.length} upstream
        </Badge>
        <Badge
          variant="light"
          color="green"
          size="sm"
          leftSection={<IconArrowRight size={12} />}
        >
          {currentTask.dependents.length} downstream
        </Badge>
        <Badge variant="light" color="gray" size="sm">
          {elements.nodes.length} total nodes
        </Badge>
      </Group>

      {/* Graph container */}
      <Paper
        shadow="xs"
        radius="sm"
        withBorder
        style={{ minHeight: 300 }}
      >
        <Box
          ref={containerRef}
          style={{ width: '100%', height: 300 }}
        />
      </Paper>

      {/* Dependency list */}
      {hasDeps && (
        <DependencyList
          title="Depends On (upstream)"
          taskIds={currentTask.dependencies}
          taskMap={taskMap}
          onTaskSelect={onTaskSelect}
          color="blue"
        />
      )}
      {hasDepnts && (
        <DependencyList
          title="Depended By (downstream)"
          taskIds={currentTask.dependents}
          taskMap={taskMap}
          onTaskSelect={onTaskSelect}
          color="green"
        />
      )}
    </Stack>
  );
}

// ============================================================================
// DEPENDENCY LIST
// ============================================================================

interface DependencyListProps {
  title: string;
  taskIds: string[];
  taskMap: Map<string, TaskFieldsFragment>;
  onTaskSelect?: (task: TaskFieldsFragment) => void;
  color: string;
}

function DependencyList({ title, taskIds, taskMap, onTaskSelect, color }: DependencyListProps) {
  return (
    <Stack gap={4} px="xs">
      <Text size="xs" fw={600} c="dimmed">{title}</Text>
      {taskIds.map(taskId => {
        const task = taskMap.get(taskId);
        return (
          <Group
            key={taskId}
            gap="xs"
            py={2}
            px="xs"
            style={{
              cursor: task && onTaskSelect ? 'pointer' : 'default',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
            onClick={() => task && onTaskSelect?.(task)}
          >
            <Badge size="xs" variant="dot" color={color}>
              {task?.status?.replace(/_/g, ' ').toLowerCase() ?? 'unknown'}
            </Badge>
            <Text size="xs" lineClamp={1}>
              {task?.name ?? taskId}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function truncateLabel(name: string, maxLen = 12): string {
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen - 1) + '…';
}
